import {isCallClosed} from '@/utils/callPresentation';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    AlertCircle, ArrowLeft, Building2, CalendarClock, CheckCircle2, CircleUserRound,
    ClipboardPenLine, Download, FileDown, FileText, Gauge, History, Laptop, MapPin, Paperclip,
    PhoneCall, Printer, RefreshCw, UserCog,
} from 'lucide-react';
import { motion } from 'framer-motion';
import api from '@/api/axios';
import { useAuth } from '@/auth/AuthContext';
import { CallDetails } from '@/types/call';
import {
    CALL_PRIORITY_LABELS, CALL_PRIORITY_STYLES, CALL_STATUS_LABELS, CALL_STATUS_STYLES,
    formatCallDate, formatCallProtocol, formatFileSize,
} from '@/utils/callPresentation';

interface InfoItemProps {
    label: string;
    value?: React.ReactNode;
    icon?: React.ReactNode;
}

const InfoItem = ({ label, value, icon }: InfoItemProps) => (
    <div className="rounded-2xl border border-white/5 bg-[#0b0f1a]/70 p-4 print-info-item">
        <div className="mb-2 flex items-center gap-2 text-[9px] font-black uppercase tracking-[0.15em] text-slate-600">
            {icon}{label}
        </div>
        <div className="break-words text-xs font-bold text-slate-200 print-info-value">{value || 'Não informado'}</div>
    </div>
);

const Section = ({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) => (
    <section className="ui-card ticket-print-section">
        <h2 className="ui-section-title mb-5 print-section-title">{icon}{title}</h2>
        {children}
    </section>
);

export default function CallDetailsPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [call, setCall] = useState<CallDetails | null>(null);
    const canContinueCall = ['ADMIN', 'TECHNICAL_ADMIN', 'TECHNICAL'].includes(user?.role || '') && !!call && !isCallClosed(call.status);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [downloadingId, setDownloadingId] = useState('');

    useEffect(() => {
        if (!id) return;
        let mounted = true;
        setLoading(true);
        api.get<CallDetails>(`/calls/${id}`)
            .then(response => mounted && setCall(response.data))
            .catch(requestError => mounted && setError(
                requestError?.response?.data?.message || 'Não foi possível carregar o chamado.',
            ))
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, [id]);

    const downloadAttachment = async (attachmentId: string, fileName: string) => {
        if (!call) return;
        setDownloadingId(attachmentId);
        try {
            const response = await api.get(`/calls/${call.id}/attachments/${attachmentId}`, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(url);
        } catch {
            setError('Não foi possível baixar o anexo.');
        } finally {
            setDownloadingId('');
        }
    };

    const generatePdf = async () => {
        if (!call) return;
        // O gerador é carregado apenas no clique para não aumentar o pacote inicial da aplicação.
        const { downloadCallPdf } = await import('@/utils/callPdfDocument');
        downloadCallPdf(call);
    };

    if (loading) {
        return <div className="page-content flex min-h-[55vh] items-center justify-center gap-3 text-sm font-bold text-blue-300"><RefreshCw className="animate-spin" size={21} /> Carregando chamado...</div>;
    }
    if (error && !call) {
        return <div className="page-content"><div className="feedback-error flex items-center gap-2"><AlertCircle size={18} /> {error}</div><button type="button" onClick={() => navigate('/tickets')} className="ui-button-secondary"><ArrowLeft size={15} /> Voltar aos chamados</button></div>;
    }
    if (!call) return null;

    return (
        <div className="page-content ticket-print-area">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="ui-card ticket-print-header">
                <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
                    <div>
                        <button type="button" onClick={() => navigate('/tickets')} className="no-print mb-4 flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-slate-500 transition hover:text-blue-300"><ArrowLeft size={14} /> Voltar à consulta</button>
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="mr-2 text-2xl font-black text-white print-title">Chamado {formatCallProtocol(call.id)}</h2>
                            <span className={`rounded-full border px-3 py-1 text-[9px] font-black uppercase tracking-wider ${CALL_STATUS_STYLES[call.status]}`}>{CALL_STATUS_LABELS[call.status]}</span>
                            <span className={`rounded-full border px-3 py-1 text-[9px] font-black uppercase tracking-wider ${CALL_PRIORITY_STYLES[call.priority]}`}>{CALL_PRIORITY_LABELS[call.priority]}</span>
                        </div>
                        <p className="mt-2 text-xs text-slate-500">Protocolo completo: <span className="font-mono text-slate-400">{call.id}</span></p>
                    </div>
                    <div className="no-print flex flex-wrap gap-3">
                        {canContinueCall && <button type="button" className="ui-button-secondary" onClick={()=>navigate(`/tickets/${call.id}/edit`)}>Editar chamado</button>}
                        {canContinueCall && <button type="button" onClick={() => navigate(`/tickets/${call.id}/progress`)} className="ui-button-primary"><ClipboardPenLine size={16} /> Atender chamado</button>}
                        <button type="button" onClick={() => window.print()} className="ui-button-secondary"><Printer size={16} /> Imprimir</button>
                        <button type="button" onClick={generatePdf} className="ui-button-secondary"><FileDown size={16} /> Gerar PDF</button>
                    </div>
                </div>
            </motion.div>

            {error && <div className="feedback-error no-print flex items-center gap-2"><AlertCircle size={17} /> {error}</div>}

            <div className="grid gap-6 xl:grid-cols-3 print-single-column">
                <div className="space-y-6 xl:col-span-2">
                    <Section title="Relato do solicitante" icon={<FileText size={16} className="text-blue-400" />}>
                        <p className="whitespace-pre-wrap text-sm leading-7 text-slate-300 print-body-text">{call.description}</p>
                    </Section>

                    <Section title="Identificação e local" icon={<CircleUserRound size={16} className="text-blue-400" />}>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <InfoItem label="Solicitante" value={call.applicant?.name} icon={<CircleUserRound size={12} />} />
                            <InfoItem label="Contato" value={call.applicant?.email || call.applicant?.phone} icon={<PhoneCall size={12} />} />
                            <InfoItem label="Unidade" value={call.unit?.name} icon={<Building2 size={12} />} />
                            <InfoItem label="Local do atendimento" value={call.local?.name} icon={<MapPin size={12} />} />
                        </div>
                    </Section>

                    <Section title="Classificação técnica" icon={<Gauge size={16} className="text-blue-400" />}>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <InfoItem label="Categoria" value={call.subcategory?.category?.name} />
                            <InfoItem label="Sintoma / serviço" value={call.subcategory?.name} />
                        </div>
                    </Section>

                    <Section title="Histórico do atendimento" icon={<History size={16} className="text-blue-400" />}>
                        {call.history.length === 0 ? <p className="text-xs text-slate-500">Nenhum registro disponível.</p> : (
                            <ol className="relative ml-2 space-y-5 border-l border-white/10 pl-5 print-history">
                                {call.history.map(entry => (
                                    <li key={entry.id} className="relative">
                                        <span className="absolute -left-[25px] top-1 h-2.5 w-2.5 rounded-full border-2 border-[#161e2d] bg-blue-500 print-history-dot" />
                                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                            <strong className="text-xs text-slate-200">{entry.responsibleUser?.name || 'Sistema'}</strong>
                                            <span className="text-[10px] text-slate-600">{formatCallDate(entry.eventDate)}</span>
                                        </div>
                                        <p className="mt-1 whitespace-pre-wrap text-[11px] leading-5 text-slate-500 print-body-text">{entry.description}</p>
                                    </li>
                                ))}
                            </ol>
                        )}
                    </Section>
                </div>

                <div className="space-y-6">
                    <Section title="Atendimento" icon={<UserCog size={16} className="text-blue-400" />}>
                        <div className="space-y-3">
                            <InfoItem label="Aberto em" value={formatCallDate(call.openingDate)} icon={<CalendarClock size={12} />} />
                            <InfoItem label="Registrado por" value={call.openedBy?.name} />
                            <InfoItem label="Técnico responsável" value={call.responsibleTechnician?.name || 'Aguardando atribuição'} />
                            <InfoItem label="Concluído em" value={call.completionDate ? formatCallDate(call.completionDate) : 'Ainda não concluído'} icon={<CheckCircle2 size={12} />} />
                        </div>
                    </Section>

                    <Section title="Equipamento" icon={<Laptop size={16} className="text-blue-400" />}>
                        {call.equipment ? (
                            <div className="space-y-3">
                                <InfoItem label="Tipo / modelo" value={[call.equipment.model?.equipmentType?.name, call.equipment.model?.name].filter(Boolean).join(' · ')} />
                                <InfoItem label="Patrimônio" value={call.equipment.patrimony} />
                                <InfoItem label="Número de série" value={call.equipment.serialNumber} />
                            </div>
                        ) : <p className="text-xs leading-5 text-slate-500">Nenhum equipamento específico foi vinculado.</p>}
                    </Section>

                    <Section title={`Anexos (${call.attachments.length})`} icon={<Paperclip size={16} className="text-blue-400" />}>
                        {call.attachments.length === 0 ? <p className="text-xs text-slate-500">Nenhum anexo registrado.</p> : (
                            <div className="space-y-2">
                                {call.attachments.map(attachment => (
                                    <button key={attachment.id} type="button" onClick={() => downloadAttachment(attachment.id, attachment.originalFileName)} disabled={downloadingId === attachment.id} className="no-print flex w-full items-center gap-3 rounded-2xl border border-white/5 bg-[#0b0f1a] p-3 text-left transition hover:border-blue-500/25 disabled:opacity-50">
                                        {downloadingId === attachment.id ? <RefreshCw size={16} className="shrink-0 animate-spin text-blue-400" /> : <Download size={16} className="shrink-0 text-blue-400" />}
                                        <span className="min-w-0 flex-1"><strong className="block truncate text-[11px] text-slate-200">{attachment.originalFileName}</strong><small className="text-[9px] text-slate-600">{formatFileSize(attachment.size)} · {formatCallDate(attachment.uploadedAt)}</small></span>
                                    </button>
                                ))}
                                <div className="hidden print:block print-attachment-list">
                                    {call.attachments.map(attachment => <p key={attachment.id}>{attachment.originalFileName} ({formatFileSize(attachment.size)})</p>)}
                                </div>
                            </div>
                        )}
                    </Section>
                </div>
            </div>
        </div>
    );
}
